import React, { useEffect, useRef } from "react";
import { useDrop } from "react-dnd";
import { Paper, Typography, Box } from "@mui/material";
import { DragItem, ItemTypes, Lead } from "../types/LeadBoardTypes";
import LeadCard from "../LeadCard/LeadCard";
import { List, CellMeasurer, CellMeasurerCache } from "react-virtualized";
import "./LeadColumn.css"; // Import custom styles

interface LeadColumnProps {
  status: string;
  leads: Lead[];
  overdueLeads?: Record<string, Lead>;
  onDrop: (id: string, newStatus: string) => void;
  onLeadDoubleClick: (lead: Lead) => void;
}

const LeadColumn: React.FC<LeadColumnProps> = React.memo(
  ({ status, leads, overdueLeads, onDrop, onLeadDoubleClick }) => {
    // Cache for dynamic row heights so longer cards don't overlap following rows
    const cacheRef = useRef(
      new CellMeasurerCache({ fixedWidth: true, defaultHeight: 260 })
    );

    // When the leads set changes (e.g., month navigation) clear measurements
    useEffect(() => {
      cacheRef.current.clearAll();
    }, [leads]);
    const [, drop] = useDrop<DragItem>(() => ({
      accept: ItemTypes.LEAD,
      drop: (item: DragItem) => {
        if (item.currentStatus !== status) {
          onDrop(item.id, status);
        }
      },
    }));

    const RenderRow = ({ index, key, style, parent }: any) => {
      const lead = leads[index];
      return (
        <CellMeasurer
          key={key}
          cache={cacheRef.current}
          parent={parent}
          columnIndex={0}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <div ref={registerChild} style={style}>
              <LeadCard
                key={lead.id}
                lead={lead}
                onDoubleClick={onLeadDoubleClick}
                overdueLeads={overdueLeads}
              />
            </div>
          )}
        </CellMeasurer>
      );
    };

    return (
      <Paper
        ref={drop}
        sx={{
          flex: 1,
          minHeight: "500px",
          margin: 1,
          backgroundColor: "#f5f5f5",
        }}
        variant="outlined"
      >
        <Typography variant="h6" align="center" gutterBottom>
          {status}
        </Typography>
        <Box
          className="lead-column-box"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
          }}
        >
          <List
            height={1000}
            rowCount={leads.length}
            deferredMeasurementCache={cacheRef.current}
            rowHeight={cacheRef.current.rowHeight}
            width={270}
            rowRenderer={RenderRow}
            overscanRowCount={5}
            data-testid={`lead-column-${status}`}
          />
        </Box>
      </Paper>
    );
  }
);

export default LeadColumn;
